import { supabase, insertRow, updateRow } from './engine';
import { reconciliarComisionesConSaco } from './comisiones';

// ──────────────────────────────────────────────────────────────
// SACO: reaplica todos los cobros de una póliza (y su cadena de
// vigencias) sobre sus saldos (tabla `pagos`), en orden estricto de
// fecha_vencimiento. Reconstruye cobro_cuotas y recalcula estados.
//
// Compatible con pólizas declarativas: sus saldos nacen de cada
// declaración/endoso (origen 'declaracion'/'endoso'/'deposito') pero
// son filas normales en `pagos` con su fecha_vencimiento, así que el
// saco las trata igual que a las cuotas tradicionales.
//
// SALDOS NEGATIVOS (devoluciones / notas de crédito): un endoso de reducción o
// cancelación, o una regularización de cierre a favor del cliente, crean filas en
// `pagos` con monto < 0 (créditos). Como RU es CORREDOR (no le devuelve dinero al
// cliente — eso lo maneja la aseguradora), un crédito NO genera una obligación de
// pago de RU: solo NETEA la deuda del cliente. Aquí los créditos se aplican como
// "dinero ya abonado" sobre las cuotas más antiguas (igual que un cobro), pero SIN
// crear filas en cobro_cuotas (no son dinero real recibido) y con el saldo del
// cliente clampado a ≥ 0. El efecto económico para RU (clawback de comisión) se
// registra aparte (ver generarComisionNegativa en src/db/comisiones.js).
// ──────────────────────────────────────────────────────────────
export async function reaplicarSacoPoliza(polizaId) {
  const r2 = n => Math.round((Number(n) || 0) * 100) / 100;

  // Cadena de vigencia: la póliza + su anterior + sus renovaciones
  const { data: pol } = await supabase.from('polizas')
    .select('id, poliza_anterior_id').eq('id', polizaId).single();
  const grupo = new Set([Number(polizaId)]);
  if (pol?.poliza_anterior_id && pol.poliza_anterior_id !== pol.id) grupo.add(pol.poliza_anterior_id);
  const { data: renovs } = await supabase.from('polizas').select('id').eq('poliza_anterior_id', polizaId);
  for (const rr of (renovs || [])) grupo.add(rr.id);
  const grupoIds = [...grupo];

  // Cobros NO anulados (orden cronológico) y cuotas (orden de vencimiento)
  const { data: cobrosRaw } = await supabase.from('cobros_recibidos')
    .select('id, monto, fecha, donde_pago, forma_pago, num_recibo_aseg, num_recibo_ru, anulado')
    .in('poliza_id', grupoIds);
  const cobros = (cobrosRaw || []).filter(c => !c.anulado)
    .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || '') || a.id - b.id);

  const { data: cuotasRaw } = await supabase.from('pagos')
    .select('id, poliza_id, monto, fecha_vencimiento, cuota_num').in('poliza_id', grupoIds);
  const todas = (cuotasRaw || [])
    .sort((a, b) => (a.fecha_vencimiento || '').localeCompare(b.fecha_vencimiento || '')
      || (a.cuota_num || 0) - (b.cuota_num || 0) || a.id - b.id);
  // Cargos = saldos ≥ 0 (eje de deuda; incluye las cuotas en $0 de declarativas sin
  // declarar, que se resetean a 'pendiente'). Créditos = filas con monto < 0 (notas de
  // crédito que netean la deuda, sin generar cobro_cuotas).
  const cuotas   = todas.filter(c => (parseFloat(c.monto) || 0) >= -0.005);
  const creditos = todas.filter(c => (parseFloat(c.monto) || 0) < -0.005);
  const totalCreditos = r2(creditos.reduce((s, c) => s + Math.abs(parseFloat(c.monto) || 0), 0));

  // Rangos acumulados (eje de dinero). Los créditos pre-abonan la deuda más antigua:
  // el dinero real (cobros) se apila ENCIMA del baseline de créditos, de modo que
  // cobro_cuotas solo refleja lo cubierto por dinero real, no por la nota de crédito.
  let accCo = totalCreditos;
  const cobroR = cobros.map(c => { const ini = accCo; accCo = r2(accCo + (parseFloat(c.monto) || 0)); return { ...c, ini, fin: accCo }; });
  let accCu = 0;
  const cuotaR = cuotas.map(c => { const ini = accCu; const m = parseFloat(c.monto) || 0; accCu = r2(accCu + m); return { ...c, ini, fin: accCu, m }; });
  // Total aplicado a la deuda = créditos (netean) + dinero real recibido. accCo ya los suma.
  const totalAplicado = accCo;

  // Limpiar vínculos viejos de TODAS las filas del grupo (cargos + créditos)
  const cuotaIds = todas.map(c => c.id);

  // Con varias vigencias en el grupo, el reparto de un cobro ENTRE pólizas puede
  // cambiar al reaplicar (se anuló un cobro anterior, entró un endoso…) y sus
  // comisiones deben seguirlo. Se guarda el reparto de ANTES para comparar al final.
  // Con una sola vigencia no hay nada que repartir: cero consultas extra.
  const hayCadena = grupoIds.length > 1;
  const polizaDeCuota = {};
  for (const c of todas) polizaDeCuota[c.id] = c.poliza_id;
  const sumarReparto = (mapa, cobroId, polId, monto) => {
    if (!cobroId || !polId) return;
    if (!mapa[cobroId]) mapa[cobroId] = {};
    mapa[cobroId][polId] = r2((mapa[cobroId][polId] || 0) + (parseFloat(monto) || 0));
  };
  const repartoAntes = {}, repartoAhora = {};
  if (hayCadena && cuotaIds.length) {
    const { data: viejos } = await supabase.from('cobro_cuotas')
      .select('cobro_id, pago_id, monto_aplicado').in('pago_id', cuotaIds);
    for (const v of (viejos || [])) sumarReparto(repartoAntes, v.cobro_id, polizaDeCuota[v.pago_id], v.monto_aplicado);
  }

  if (cuotaIds.length) await supabase.from('cobro_cuotas').delete().in('pago_id', cuotaIds);

  // Re-aplicar saco sobre cada cuota + reconstruir cobro_cuotas por solapamiento de rangos
  for (const cu of cuotaR) {
    // Cuota en $0 (ej. mes de una declarativa aún sin declarar): queda 'pendiente'
    // sin pago aplicado — NO se marca 'pagado' ni se le ponen fecha/recibo de cobro.
    if (cu.m <= 0.005) {
      await updateRow('pagos', cu.id, {
        estado: 'pendiente', monto_pagado: 0,
        fecha_pago: null, donde_pago: null, forma_pago_real: null,
        num_recibo_aseg: null, num_recibo_ru: null,
      });
      continue;
    }
    // Cobertura = créditos (netean deuda) + dinero real, clampada al monto de la cuota.
    const pagado = r2(Math.max(0, Math.min(cu.m, r2(totalAplicado - cu.ini))));
    let estado, montoPagado, meta = null;
    if (pagado >= cu.m - 0.01) { estado = 'pagado'; montoPagado = cu.m; }
    else if (pagado > 0.01)    { estado = 'parcial'; montoPagado = pagado; }
    else                       { estado = 'pendiente'; montoPagado = 0; }
    if (estado !== 'pendiente') {
      const solapan = cobroR.filter(co => Math.min(co.fin, cu.fin) - Math.max(co.ini, cu.ini) > 0.005);
      meta = solapan.length ? solapan[solapan.length - 1] : null; // último cobro (por fecha) que la cubre
    }
    await updateRow('pagos', cu.id, {
      estado, monto_pagado: montoPagado,
      fecha_pago:      meta ? meta.fecha : null,
      donde_pago:      meta ? meta.donde_pago : null,
      forma_pago_real: meta ? meta.forma_pago : null,
      num_recibo_aseg: meta ? meta.num_recibo_aseg : null,
      num_recibo_ru:   meta ? meta.num_recibo_ru : null,
    });
    for (const co of cobroR) {
      const aplicado = r2(Math.min(co.fin, cu.fin) - Math.max(co.ini, cu.ini));
      if (aplicado > 0.005) {
        await insertRow('cobro_cuotas', { cobro_id: co.id, pago_id: cu.id, monto_aplicado: aplicado });
        if (hayCadena) sumarReparto(repartoAhora, co.id, cu.poliza_id, aplicado);
      }
    }
  }

  // El saco ya quedó bien aplicado: un fallo al ajustar comisiones no debe tumbar la
  // operación que lo llamó (endoso, anulación, período…). Se deja en consola.
  if (hayCadena) {
    try { await reconciliarComisionesConSaco({ cobros, repartoAntes, repartoAhora }); }
    catch (e) { console.error('reconciliar comisiones con el saco', e); }
  }
}
